
import React from "react";
import type { FileMeta } from "../components/FileUploader";

export type AiSubject = {
  type: "contract" | "case" | "dispute" | "brandAsset" | "task" | "govCorrespondence" | "signRequest" | "sealRequest" | "other";
  id: string; // Business ID (e.g. CT-xxx)
  title: string;
  data: any; // Full record
  attachments?: FileMeta[];
  
  // Callback for applying AI results back to the form
  onApplyResult?: (target: string, content: string) => void;
};

export type FormDraft = {
  type: string;
  data: any;
  onApplyTitle?: (title: string) => void;
  onApplyNotes?: (notes: string) => void;
  // Generic apply
  onApplyResult?: (target: string, content: string) => void;
};

export type DeleteRequest = {
  targetType: string;
  targetId: string;
  summary: string;
  onConfirm: () => void;
};

type AiContextType = {
  // AI Selection (Existing)
  activeItem: AiSubject | null;
  setActiveItem: (item: AiSubject | null) => void;
  
  // AI Visibility
  isAiOpen: boolean;
  setAiOpen: (open: boolean) => void;

  // Form Draft (New)
  activeDraft: FormDraft | null;
  setDraft: (draft: FormDraft | null) => void;

  // Global Delete (New)
  requestDelete: (req: DeleteRequest) => void;
  deleteDialogState: DeleteRequest | null;
  closeDeleteDialog: () => void;
};

export const AiContext = React.createContext<AiContextType>({
  activeItem: null,
  setActiveItem: () => {},
  isAiOpen: false,
  setAiOpen: () => {},
  activeDraft: null,
  setDraft: () => {},
  requestDelete: () => {},
  deleteDialogState: null,
  closeDeleteDialog: () => {},
});

export const useAiContext = () => React.useContext(AiContext);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeItem, setActiveItem] = React.useState<AiSubject | null>(null);
  const [isAiOpen, setAiOpen] = React.useState(false);
  const [activeDraft, setActiveDraft] = React.useState<FormDraft | null>(null);
  
  const [deleteDialogState, setDeleteDialogState] = React.useState<DeleteRequest | null>(null);

  const requestDelete = (req: DeleteRequest) => {
    setDeleteDialogState(req);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogState(null);
  };

  const setDraft = (draft: FormDraft | null) => {
    setActiveDraft(draft);
  };

  return (
    <AiContext.Provider value={{
      activeItem, setActiveItem,
      isAiOpen, setAiOpen,
      activeDraft, setDraft,
      requestDelete, deleteDialogState, closeDeleteDialog
    }}>
      {children}
    </AiContext.Provider>
  );
};
